import clsx from "clsx";
import { useEffect, useState, useRef, useMemo } from "preact/hooks";
import { lazy, Suspense } from "preact/compat";
import { Localizer, Text, useText } from "preact-i18n";
import { Helmet } from "react-helmet";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { Button, Skeleton, Tooltip } from "@mui/material";
import { useMatomo } from "@datapunt/matomo-tracker-react";

import useTypewriterEffect, { getTypewriter } from "use-typewriter-effect";

import LoginButton from "../LoginButton/LoginButton";
import { AddressInput, SignupForm } from "../UsernamePage/UsernamePage";
import { StatusDot } from "../Header/Header";

dayjs.extend(relativeTime);

import style from "./style.module.css";

const DashboardDemo = lazy(() => import("../DashboardDemo/DashboardDemo"));

const Question = ({ question, answer }) => {
  return (
    <div class="mb-4">
      <h5 class="text-xl md:text-2xl font-bold leading-tighter tracking-tighter mb-1">
        {question}
      </h5>
      <p dangerouslySetInnerHTML={{ __html: answer }} />
    </div>
  );
};

const FAQs = [
  [
    "Can I cancel anytime?",
    "Yes, you can cancel anytime. The subscription will run out with the end of the payment period, which will be no longer than one month.",
  ],
  [
    "How is my privacy protected?",
    "The Wasc.me email client blocks email trackers, so the sender doesn't know whether you have read it. The client resolves tracking links, which are meant to show the sender, what links you clicked on, and replaces them with the direct target, so no tracking can occur. Additionally, we don't collect any more personal information than legally required, follow all GDPR requests and will be happy to answer any further questions.",
  ],
  [
    "Can I connect a mobile client?",
    'Yes, you can, for example, fetch your emails from an Apple Mail client. Head to the <a class="text-blue-400 underline" href="/dashboard/settings/application-passwords" target="_blank" rel="noopener norefererf">Application Passwords</a> dashboard, generate new credentials and configure your email client with your primary address and the newly generated password via SMTP and IMAP. Note however, that most of the privacy features, such as anti-tracking, link-deobfuscation, and aliases aren\'t available on third-party clients.',
  ],
  [
    "What are Obfuscation Aliases?",
    "An Obfuscation Alias is our idea, on how to protect your privacy further. Instead of giving out your primary email address to every newsletter you sign up to, generate a new alias.You'll create a new address, like river_best_take@wasc.me which forwards all received messages to your Inbox. The key is, the outside service never gets to see your actual address, all replies are sent through the alias. Additionally, if the signup turns out to be uncancelable spam, you can just delete the alias and the service will never be able to reach you again.",
  ],
  [
    "How much does it cost?",
    `We charge 2,99 € / month. At the moment there is only one plan, however that might change in the future, depending on demand of features and demand of prices. If you're looking for other options, please <a class="text-blue-400 underline" href="mailto:support@wasc.io?subject=Wasc%20Mail%20Pricing" target="_blank">send us an email</a> and tell us about your idea!`,
  ],
  [
    "How are you handling my data?",
    "All of the email data is encrypted at rest, regularly saved through backups and accessible only by you.",
  ],
  [
    "Any questions left?",
    'Please <a class="text-blue-400 underline" href="mailto:support@wasc.io?subject=Wasc%20Mail%20Learn%20More&body=What\'s%20your%20question" target="_blank">send us an email</a> and we will get back to you shortly.',
  ],
];

const FAQLD = () => {
  return (
    <Helmet>
      <title>Your email privacy protected - Wasc.me</title>

      <script type="application/ld+json">
        {`{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [${FAQs.map((faq) =>
            JSON.stringify({
              "@type": "Question",
              name: faq[0],
              acceptedAnswer: {
                "@type": "Answer",
                text: faq[1],
              },
            })
          )}]
        }`}
      </script>
    </Helmet>
  );
};

const CohortMessage = () => {
  // TODO i18n date
  const nextMonday = useMemo(() => {
    var date = new Date();
    date.setDate(date.getDate() + ((1 + 7 - date.getDay()) % 7 || 7));

    return dayjs(date).fromNow();
  }, []);

  return (
    <span class="italic text-sm underline">
      <Text id="Main.nextCohort" fields={{ relativeTime: nextMonday }} />
    </span>
  );
};

const USERNAME_SUGGESTIONS = [
  "nicolai",
  "Batman",
  "Queen.of.England",
  "dariush",
  "Caesar",
  "Nassim.Taleb",
];

const Main = () => {
  const { trackPageView, trackEvent } = useMatomo();
  const [interacted, setInteracted] = useState();
  const [email, setEmail] = useState();
  const [invalidEmail, setInvalidEmail] = useState("");

  const [state, dispatch, isTyping] = useTypewriterEffect();

  const inputRef = useRef(null);

  useEffect(() => {
    USERNAME_SUGGESTIONS.reduce(
      (previous, current) => previous.type(current).pauseFor(1000).deleteAll(),
      getTypewriter(dispatch)
    )
      .setLoop(true)
      .trigger();
  }, [dispatch]);

  // Autofocus input
  useEffect(() => {
    inputRef?.current.focus();
  }, [inputRef]);

  useEffect(() => {
    trackPageView();
  }, []);

  const submitDisabled = !Boolean(email) || Boolean(invalidEmail);

  return (
    <>
      <FAQLD />
      <div class="pl-4 pt-4 fixed top-0 left-0">
        <StatusDot />
      </div>
      <div class="pr-4 pt-4 fixed top-0 right-0">
        <LoginButton
          onClick={() => {
            trackEvent({ category: "main-landingpage", action: "click:login" });
          }}
        />
      </div>
      <div class={clsx(style.wrapper, "w-full h-full overflow-y-auto")}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full">
          {/* Hero content */}
          <div className="pt-32 pb-12 md:pt-40 md:pb-4">
            {/* Section header */}
            <div className="text-center pb-12 md:pb-16">
              <h1
                className="text-3xl sm:text-5xl md:text-6xl font-extrabold leading-tighter tracking-tighter mb-4"
                data-aos="zoom-y-out"
              >
                <Text id="Main.tagline.privacy" />{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600">
                  <Text id="Main.tagline.protected" />
                </span>
              </h1>
              <div className="max-w-3xl mx-auto">
                <p className="text-xl text-gray-600">
                  <Text id="Main.subline" />
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  <Text id="Main.subsubline" />
                  <br />
                  <a
                    variant="outlined"
                    class="underline text-blue-400 bold"
                    href="#faq"
                    onClick={() => {
                      trackEvent({
                        category: "main-landingpage",
                        action: "click:FAQ",
                      });
                    }}
                  >
                    <Text id="Main.FAQLine" />
                  </a>
                </p>

                <div className="max-w-xs mx-auto sm:max-w-none sm:flex sm:justify-center w-full">
                  <SignupForm email={email} class="w-full">
                    <div class="w-full flex flex-col justify-center sm:flex-row">
                      <AddressInput
                        placeholder={state}
                        InputLabelProps={{ shrink: true }}
                        className="sm:mr-2"
                        inputRef={inputRef}
                        onChange={(event) => {
                          if (!interacted) {
                            trackEvent({
                              category: "signup",
                              action: "funnel:signup:enteredUsername",
                            });
                            setInteracted(true);
                          }

                          setEmail(event.target.value);
                        }}
                        onValidChange={(invalid) => setInvalidEmail(invalid)}
                      />

                      <div class="h-full mt-2 sm:mt-0 sm:ml-4">
                        <Localizer>
                          <Tooltip
                            title={
                              submitDisabled ? (
                                <Text id="Main.chooseUsername" />
                              ) : (
                                <Text id="Main.signupTooltip" />
                              )
                            }
                          >
                            <span class="w-full md:w-auto">
                              <Button
                                type="submit"
                                variant="outlined"
                                // TODO Button becomes huge on taken username
                                className="w-full h-full md:w-auto"
                                disabled={submitDisabled}
                                onClick={() => {
                                  trackEvent({
                                    category: "main-landingpage",
                                    action: "funnel:signup:free-trial",
                                  });
                                }}
                              >
                                <Text id="Main.startTrial" />
                              </Button>
                            </span>
                          </Tooltip>
                        </Localizer>
                      </div>
                    </div>
                    <div>
                      <CohortMessage />
                    </div>
                  </SignupForm>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div class="w-full flex flex-col justify-center items-center mb-20">
          <a href="#features">
            <h3 class="text-3xl py-4 mb-6 font-bold">
              <Text id="Main.howProtect" />
            </h3>
          </a>

          <div class="w-11/12 sm:w-5/6 md:w-3/4 mx-3 md:mx-0 drop-shadow-lg md:drop-shadow-2xl bg-white rounded-lg">
            {/* Header */}
            <div className="w-full h-16 shadow p-3 flex justify-between items-center">
              <h1 class="whitespace-nowrap text-lg ml-4">Wasc Mail</h1>
              <div className="w-full mx-16" />
              <div>
                <span class="rounded-full w-8 h-8 flex items-center justify-center border border-blue-400 bg-blue-400 text-white">
                  N
                </span>
              </div>
            </div>

            <div class="px-3 pb-4 pt-4 md:px-12 md:pb-8 ">
              <Suspense
                fallback={
                  <div>
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                  </div>
                }
              >
                <DashboardDemo
                  onFinish={() => {
                    inputRef?.current.focus();
                    inputRef?.current.scrollIntoView({
                      block: "center",
                      behavior: "smooth",
                    });
                  }}
                />
              </Suspense>
            </div>
          </div>
        </div>

        {/* TODO Lazy load */}
        {/* FAQ */}
        <div class="p-4 w-full h-full md:grid md:grid-cols-6">
          <div class="md:col-span-1">
            <div class="md:sticky md:top-3 w-full flex justify-center">
              <h4 class="text-2xl md:text-3xl font-bold leading-tighter tracking-tighter mb-4">
                <a href="#faq">
                  <Text id="Main.FAQ" />
                </a>
              </h4>
            </div>
          </div>
          <div class="col-span-6 md:col-span-5">
            <a id="faq" />
            {FAQs.map((faq) => (
              <Question key={faq[0]} question={faq[0]} answer={faq[1]} />
            ))}
          </div>
        </div>

        <div class="p-2 md:p-3 w-full inline-flex justify-start bg-gray-100">
          <ul class="w-full inline-flex justify-between">
            <li class="flex flex-col md:block">
              <a
                href="https://short.wasc.me"
                target="_blank"
                rel="noreferrer"
                class="text-blue-400 underline"
              >
                <Text id="Main.WascShort" />
              </a>
            </li>
            <li class="flex flex-col list-disc md:list-none text-right pr-3 md:pr-0 md:block md:text-left">
              <a
                href="https://wasc.io/imprint-privacy"
                target="_blank"
                rel="noreferrer"
                class="text-blue-400 underline"
              >
                <Text id="Main.imprintPrivacy" />
              </a>

              <a
                href="https://wasc.io/terms"
                target="_blank"
                rel="noreferrer"
                class="text-blue-400 underline ml-2"
              >
                <Text id="Main.terms" />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Main;
